import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Row, Col, Typography, Button } from 'antd';
import { useParams } from 'react-router-dom';
import { fetchViewQuotationById } from '../../slices/quotation';
import { useDispatch } from 'react-redux';
import { ReactComponent as PrintIcon } from '../../Images/PrintIcon.svg';
import dayjs from 'dayjs';
import logo from '../../Images/logo.svg';

const PrintQuotation3 = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const [quotationData, setQuotationData] = useState(null);
    const contentRef = useRef();

    const fetchData = useCallback(async () => {
        try {
            const response = await dispatch(
                fetchViewQuotationById({ id, print: true }),
            ).unwrap();
            setQuotationData(response.results.data[0]);
        } catch (error) {
            console.error('Failed to fetch quotation:', error);
        }
    }, [dispatch, id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const { Title, Text } = Typography;

    const hasSurfaceTreatment = quotationData?.quotation_details?.some(item =>
        item?.jobworks?.includes('Surface treatment'),
    );
    const hasAnodising = quotationData?.quotation_details?.some(
        item => item?.jobworks?.includes('Surface treatment') && item?.anodising,
    );
    const hasPowderCoating = quotationData?.quotation_details?.some(
        item =>
            item?.jobworks?.includes('Surface treatment') && item?.powder_coating,
    );
    const hasPVDF = quotationData?.quotation_details?.some(
        item => item?.jobworks?.includes('Surface treatment') && item?.pvdf,
    );
    const hasEngineeringChildren = quotationData?.quotation_details?.some(item =>
        item?.jobworks?.includes('Engineering'),
    );
    const hascutting = quotationData?.quotation_details?.some(
        item => item?.jobworks?.includes('Engineering') && item?.cutting,
    );
    const hasMachinings = quotationData?.quotation_details?.some(
        item => item?.jobworks?.includes('Engineering') && item?.machinings,
    );
    const hasDeburring = quotationData?.quotation_details?.some(
        item => item?.jobworks?.includes('Engineering') && item?.deburring,
    );
    const hasLaserChildren = quotationData?.quotation_details?.some(item =>
        item?.jobworks?.includes('Laser marking'),
    );
    const hasLaserDescription = quotationData?.quotation_details?.some(
        item =>
            item?.jobworks?.includes('Laser marking') &&
            item?.laser_marking_description,
    );
    const hasPrice = quotationData?.quotation_details?.some(
        item =>
            item?.jobworks?.includes('Laser marking') && item?.laser_marking_price,
    );

    const getJobWorkOrder = () => {
        const jobWorkOrder = [];
        const seenJobWorks = new Set();

        quotationData?.quotation_details?.forEach(item => {
            if (Array.isArray(item.jobworks)) {
                item.jobworks.forEach(job => {
                    if (!seenJobWorks.has(job)) {
                        jobWorkOrder.push(job);
                        seenJobWorks.add(job);
                    }
                });
            }
        });

        return jobWorkOrder;
    };

    const hasMillFinish = quotationData?.quotation_details?.some(item =>
        item?.jobworks?.includes('Mill Finish'),
    );
    const hasOutSource = quotationData?.quotation_details?.some(item =>
        item?.jobworks?.includes('Out Source'),
    );
    const hasThermalBreak = quotationData?.quotation_details?.some(item =>
        item?.jobworks?.includes('Thermal Break'),
    );

    const hasOnlyMillFinish = quotationData?.quotation_details?.every(item => {
        return (
            Array.isArray(item.jobworks) &&
            item.jobworks.length === 1 &&
            item.jobworks[0] === 'Mill Finish'
        );
    });

    const surfaceTreatmentChildren = [];
    const EngineeringChildren = [];
    const LaserChildren = [];

    if (hasAnodising) {
        surfaceTreatmentChildren.push(
            {
                title: 'Anodising Price',
                dataIndex: 'anodising_price',
                key: 'anodising_price',
                align: 'center',
                width: 65,
                render: (_, record) =>
                    record.jobworks.includes('Surface treatment') && record.anodising
                        ? _
                        : null,
            },
            {
                title: 'Anodising Description',
                dataIndex: 'anodising_description',
                key: 'anodising_description',
                align: 'center',
                width: 75,
                render: (_, record) =>
                    record.jobworks.includes('Surface treatment') && record.anodising
                        ? _
                        : null,
            },
        );
    }

    if (hasPowderCoating) {
        surfaceTreatmentChildren.push(
            {
                title: 'Powder Coating Price',
                dataIndex: 'powder_coating_price',
                key: 'powder_coating_price',
                align: 'center',
                width: 60,
                render: (_, record) =>
                    record.jobworks.includes('Surface treatment') && record.powder_coating
                        ? _
                        : null,
            },
            {
                title: 'Powder Coating Description',
                dataIndex: 'powder_coating_description',
                key: 'powder_coating_description',
                align: 'center',
                width: 60,
                render: (_, record) =>
                    record.jobworks.includes('Surface treatment') && record.powder_coating
                        ? _
                        : null,
            },
        );
    }

    if (hasPVDF) {
        surfaceTreatmentChildren.push(
            {
                title: 'PVDF Price',
                dataIndex: 'pvdf_price',
                key: 'pvdf_price',
                align: 'center',
                width: 55,
                render: (_, record) =>
                    record.jobworks.includes('Surface treatment') && record.pvdf
                        ? _
                        : null,
            },
            {
                title: 'PVDF Description',
                dataIndex: 'pvdf_description',
                key: 'pvdf_description',
                align: 'center',
                width: 60,
                render: (_, record) =>
                    record.jobworks.includes('Surface treatment') && record.pvdf
                        ? _
                        : null,
            },
        );
    }

    if (hascutting) {
        EngineeringChildren.push({
            title: 'Cutting Price',
            dataIndex: 'cutting_price',
            key: 'cutting_price',
            align: 'center',
            width: 60,
            render: (_, record) =>
                record.jobworks.includes('Engineering') && record.cutting ? _ : null,
        });
    }

    if (hasMachinings) {
        EngineeringChildren.push({
            title: 'Machinings Price',
            dataIndex: 'machinings_price',
            key: 'machinings_price',
            align: 'center',
            width: 60,
            render: (_, record) =>
                record.jobworks.includes('Engineering') && record.machinings ? _ : null,
        });
    }

    if (hasDeburring) {
        EngineeringChildren.push({
            title: 'Deburring Price',
            dataIndex: 'deburring_price',
            key: 'deburring_price',
            align: 'center',
            width: 60,
            render: (_, record) =>
                record.jobworks.includes('Engineering') && record.deburring ? _ : null,
        });
    }

    if (hasLaserDescription) {
        LaserChildren.push({
            title: 'Laser Marking Description',
            dataIndex: 'laser_marking_description',
            key: 'laser_marking_description',
            align: 'center',
            width: 60,
            render: (_, record) =>
                record.jobworks.includes('Laser marking') &&
                    record.laser_marking_description
                    ? _
                    : null,
        });
    }

    if (hasPrice) {
        LaserChildren.push({
            title: 'Laser Marking Price',
            dataIndex: 'laser_marking_price',
            key: 'laser_marking_price',
            align: 'center',
            width: 60,
            render: (_, record) =>
                record.jobworks.includes('Laser marking') && record.laser_marking_price
                    ? _
                    : null,
        });
    }

    const jobWorkLeafCount =
        surfaceTreatmentChildren.length +
        EngineeringChildren.length +
        LaserChildren.length +
        (hasMillFinish ? 1 : 0) +
        (hasOutSource ? 1 : 0) +
        (hasThermalBreak ? 1 : 0);

    const millFinishColumn = hasMillFinish
        ? {
            title: 'Mill Finish',
            align: 'center',
            width: 50,
            dataIndex: 'mill_finish',
            key: 'mill_finish',
            render: (_, record) => {
                return record.jobworks.includes('Mill Finish') ? 'Yes' : null;
            },
        }
        : null;

    const engineeringColumn = hasEngineeringChildren
        ? {
            title: 'Engineering',
            align: 'center',
            width: 150,
            children: EngineeringChildren,
        }
        : null;

    const surfaceTreatmentColumn = hasSurfaceTreatment
        ? {
            title: 'Surface Treatment',
            align: 'center',
            width: 200,
            children: surfaceTreatmentChildren,
        }
        : null;

    const outSourceColumn = hasOutSource
        ? {
            title: 'Out Source',
            align: 'center',
            width: 50,
            dataIndex: 'out_source',
            key: 'out_source',
            render: (_, record) => {
                return record.jobworks.includes('Out Source') &&
                    record.out_source === true
                    ? 'Yes'
                    : record.jobworks.includes('Out Source') &&
                        record.out_source === false
                        ? 'No'
                        : '';
            },
        }
        : null;

    const laserMarkingColumn = hasLaserChildren
        ? {
            title: 'Laser Marking',
            align: 'center',
            width: 150,
            children: LaserChildren,
        }
        : null;

    const thermalBreakColumn = hasThermalBreak
        ? {
            title: 'Thermal Break',
            align: 'center',
            width: 50,
            dataIndex: 'thermal_break',
            key: 'thermal_break',
            render: (_, record) => {
                return record.jobworks.includes('Thermal Break') ? 'Yes' : null;
            },
        }
        : null;

    const jobWorkOrder = getJobWorkOrder();
    const jobWorkColumnMap = {
        'Mill Finish': millFinishColumn,
        Engineering: engineeringColumn,
        'Surface treatment': surfaceTreatmentColumn,
        'Out Source': outSourceColumn,
        'Laser Marking': laserMarkingColumn,
        'Thermal Break': thermalBreakColumn,
    };

    const orderedJobWorkChildren = jobWorkOrder
        .map(name => jobWorkColumnMap[name])
        .filter(Boolean);

    const jobWorkColumns = !hasOnlyMillFinish
        ? [
            {
                title: 'Job Work',
                align: 'center',
                width: jobWorkLeafCount * 50,
                children: orderedJobWorkChildren,
            },
        ].filter(Boolean)
        : [];

    const columns = [
        {
            title: 'SR No.',
            dataIndex: 'sr',
            width: 40,
            align: 'center',
            className: 'col-sr',
            render: (_text, _record, index) => index + 1,
        },
        {
            title: 'PROFILE',
            dataIndex: 'section',
            width: 60,
            align: 'center',
            className: 'col-profile',
            render: (_text, _record) => (
                <span>{_record?.die_profile?.die_number || 'N/A'}</span>
            ),
        },
        {
            title: 'Cust Ref No.',
            dataIndex: 'customer_reference_no',
            key: 'customer_reference_no',
            align: 'center',
            width: 65,
        },
        {
            title: 'IMAGE',
            dataIndex: 'image',
            width: 80,
            align: 'center',
            className: 'summary-cell-center col-image',
            render: (_text, _record) => {
                const fileUrl = _record?.die_profile?.die_diagram;
                const imageContainerStyle = {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '4px',
                    overflow: 'hidden',
                };
                const imageStyle = {
                    width: '60px',
                    height: '60px',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    display: 'block',
                };
                if (!fileUrl) {
                    return (
                        <div style={imageContainerStyle}>
                            <span style={{ fontSize: '16px', color: '#999' }}>N/A</span>
                        </div>
                    );
                }
                const fileExtension = fileUrl.split('.').pop().toLowerCase();
                if (fileExtension === 'pdf') {
                    return (
                        <Button
                            type="link"
                            style={{ padding: '0px' }}
                            onClick={async () => {
                                const response = await fetch(fileUrl);
                                if (!response.ok) {
                                    throw new Error(`HTTP error! Status: ${response.status}`);
                                }
                                const blobData = await response.blob();
                                const blob = new Blob([blobData], { type: 'application/pdf' });
                                const blobURL = URL.createObjectURL(blob);
                                window.open(blobURL);
                            }}
                        >
                            View PDF
                        </Button>
                    );
                } else if (
                    ['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(fileExtension)
                ) {
                    return (
                        <div style={imageContainerStyle}>
                            <img src={fileUrl} alt="Die Diagram" style={imageStyle} />
                        </div>
                    );
                } else {
                    return (
                        <div style={imageContainerStyle}>
                            <span style={{ fontSize: '12px', color: '#999' }}>Unsupported</span>
                        </div>
                    );
                }
            },
        },
        {
            title: 'DESCRIPTION',
            dataIndex: 'description',
            width: 90,
            align: 'center',
            className: 'col-desc',
            render: (_text, _record) => {
                const { dimension1, dimension2, dimension3, dimension4 } =
                    _record?.die_profile || {};
                const formattedDimensions = [
                    dimension1,
                    dimension2,
                    dimension3,
                    dimension4,
                ]
                    .map((dim, index) => {
                        if (dim === null || dim === '' || dim === undefined) return null;
                        const fixed = Number(dim)?.toFixed(1);
                        if ((index === 2 || index === 3) && fixed === '0.0') return '';
                        return fixed;
                    })
                    .filter(dim => dim !== null && dim !== '');
                if (formattedDimensions.length === 0) return <span>N/A</span>;
                return <div>{formattedDimensions.join(' X ')}</div>;
            },
        },
        {
            title: 'LENGTH (mm)',
            dataIndex: 'length',
            width: 55,
            align: 'center',
            className: 'col-length',
            render: value => parseInt(value)?.toFixed(0),
        },
        {
            title: 'QUANTITY',
            width: 130,
            children: [
                {
                    title: 'PIECES',
                    dataIndex: 'pieces',
                    width: 30,
                    align: 'center',
                    className: 'col-pieces',
                },
                {
                    title: 'WEIGHT (kg.)',
                    dataIndex: 'net_weight',
                    width: 30,
                    align: 'center',
                    className: 'col-weight',
                    render: value => parseFloat(value)?.toFixed(3),
                },
            ],
        },
        {
            title: 'ALLOY/TEMP',
            dataIndex: 'grade',
            width: 95,
            align: 'center',
            className: 'col-alloy',
            render: (_text, _record) => (
                <div>
                    {_record.alloy?.name || ''} / {_record.temper?.code || ''}
                </div>
            ),
        },
        ...jobWorkColumns,
        {
            title: 'PRICE/KG (Rs.)',
            width: 150,
            children: [
                {
                    title: 'Item Rate',
                    dataIndex: 'conversion',
                    width: 50,
                    className: 'col-conv',
                    align: 'center',
                    render: (_, record) => {
                        const pricePerKg = parseFloat(record.price_per_kg ?? 0);
                        const conversion = parseFloat(record.conversion ?? 0);
                        const sum = (pricePerKg + conversion)?.toFixed(2);
                        return <span>{sum}</span>;
                    },
                },
                {
                    title: 'Other',
                    dataIndex: 'otherRate',
                    width: 40,
                    className: 'col-other',
                    align: 'center',
                    render: () => <span>0.00</span>,
                },
            ],
        },
        {
            title: 'BASIC AMOUNT',
            dataIndex: 'total_basic_amount',
            width: 90,
            className: 'col-basic',
            align: 'right',
            render: total_basic_amount => (
                <span>
                    {!isNaN(total_basic_amount)
                        ? Number(total_basic_amount)?.toFixed(2)
                        : ''}
                </span>
            ),
        },
        {
            title: 'GST',
            align: 'center',
            width: 80,
            children: [
                {
                    title: 'IGST (18%)',
                    dataIndex: 'gst_rate',
                    key: 'gst_rate',
                    width: 50,
                    className: 'col-gst',
                    align: 'right',
                    render: gst_rate => (
                        <span>{!isNaN(gst_rate) ? Number(gst_rate)?.toFixed(2) : ''}</span>
                    ),
                },
            ],
        },
        {
            title: 'TOTAL',
            dataIndex: 'total_amount',
            width: 80,
            className: 'col-total',
            align: 'right',
            render: (_text, record) => {
                return <span>{record?.total_amount?.toFixed(2) || ''}</span>;
            },
        },
    ];

    const getMaxColumnDepth = columns => {
        let maxDepth = 1;
        columns.forEach(col => {
            if (col.children) {
                const childDepth = getMaxColumnDepth(col.children) + 1;
                maxDepth = Math.max(maxDepth, childDepth);
            }
        });
        return maxDepth;
    };

    const renderHeaderRows = () => {
        const maxDepth = getMaxColumnDepth(columns);
        const headerRows = [];

        const collectHeadersAtDepth = (cols, depth, currentDepth = 0) => {
            const headers = [];

            cols.forEach((col, index) => {
                const colSpan = getColSpan(col);

                if (currentDepth === depth) {
                    headers.push(
                        <th
                            key={col.key || index}
                            colSpan={colSpan}
                            rowSpan={col.children ? 1 : maxDepth - currentDepth}
                            style={{
                                border: '1px solid #dee2e6',
                                padding: '12px 8px',
                                backgroundColor: '#f8f9fa',
                                textAlign: 'center',
                                fontWeight: '600',
                                fontSize: '14px'
                            }}
                        >
                            {typeof col.title === 'function' ? col.title() : col.title}
                        </th>,
                    );
                } else if (col.children) {
                    headers.push(
                        ...collectHeadersAtDepth(col.children, depth, currentDepth + 1),
                    );
                }
            });

            return headers;
        };

        for (let depth = 0; depth < maxDepth; depth++) {
            headerRows.push(
                <tr key={`header-row-${depth}`} style={{ backgroundColor: '#f8f9fa' }}>
                    {collectHeadersAtDepth(columns, depth)}
                </tr>,
            );
        }

        return headerRows;
    };

    const getColSpan = column => {
        if (column.children) {
            return column.children.reduce((sum, child) => sum + getColSpan(child), 0);
        }
        return 1;
    };

    const renderCellContent = (column, record, index) => {
        if (column.render) {
            const result = column.render(record[column.dataIndex], record, index);
            return React.isValidElement(result) ? result : String(result || '');
        }
        return record[column.dataIndex] || '';
    };

    const getLeafColumns = () => {
        const leafColumns = [];

        columns.forEach(column => {
            if (column.children) {
                column.children.forEach(child => {
                    if (child.children) {
                        leafColumns.push(...child.children);
                    } else {
                        leafColumns.push(child);
                    }
                });
            } else {
                leafColumns.push(column);
            }
        });

        return leafColumns;
    };

    const leafColumns = getLeafColumns();

    const printStyles = `
        @media print {
            * {
                margin: 0 !important;
                padding: 0 !important;
                box-sizing: border-box !important;
            }
            
            body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
                font-size: 12px !important;
                line-height: 1.4 !important;
                color: #000 !important;
                background: white !important;
            }
            
            .print-container-quotation {
                padding: 20px !important;
                width: 100% !important;
                max-width: none !important;
                margin: 0 !important;
                background: white !important;
                box-shadow: none !important;
            }
            
            .terms-and-bank-container {
                padding: 20px !important;
                width: 100% !important;
                page-break-before: always !important;
            }
            
            @page {
                size: A4 !important;
                margin: 15mm !important;
            }
            
            .main-heading {
                font-size: 28px !important;
                font-weight: 600 !important;
                text-align: center !important;
                margin: 20px 0 !important;
                color: #000 !important;
            }
            
            .header-text {
                font-size: 14px !important;
                line-height: 1.5 !important;
            }
            
            .customer-details-header {
                font-weight: 600 !important;
                font-size: 16px !important;
                margin-bottom: 8px !important;
                color: #333 !important;
            }
            
            table {
                width: 100% !important;
                border-collapse: collapse !important;
                margin: 20px 0 !important;
                border: 1px solid #c0bfbfff !important;
            }
            
            table th,
            table td {
                border: 1px solid #c0bfbfff !important;
                padding: 8px 6px !important;
                text-align: center !important;
                vertical-align: middle !important;
                font-size: 11px !important;
                word-wrap: break-word !important;
            }
            
            table thead th {
                background-color: #ffffffff !important;
                font-weight: 600 !important;
                font-size: 12px !important;
                border-bottom: 2px solid #c0bfbfff !important;
            }
            
            table thead {
                display: table-header-group !important;
            }
            
            .summary-row td {
                background-color: #ffffffff !important;
                font-weight: 600 !important;
                font-size: 12px !important;
            }
            
            .signature-section {
                margin-top: 60px !important;
                display: flex !important;
                justify-content: space-between !important;
            }
            
            .signature-section h5 {
                font-size: 14px !important;
                color: #12406a !important;
                margin-top: 40px !important;
            }
            
            .terms-heading {
                font-size: 18px !important;
                font-weight: 700 !important;
                text-align: center !important;
                color: #12406a !important;
                margin-bottom: 15px !important;
            }
            
            .terms__content {
                font-size: 10px !important;
                line-height: 1.1 !important;
                text-align: justify !important;
            }
            
            .bankDetails {
                background: #ffffffff !important;
                padding: 15px !important;
                border-left: 4px solid #ffffffff !important;
                margin-top: 20px !important;
            }
            
            .bankDetails b {
                font-weight: 600 !important;
            }
            
            .no-print {
                display: none !important;
            }
            
            .logo-section img {
                max-width: 180px !important;
                height: auto !important;
            }
            
            .ant-row {
                display: flex !important;
                width: 100% !important;
            }
            
            .ant-col {
                flex: 1 !important;
            }
        }
    `;

    const commonStyles = `
        * {
          font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji !important;
        }
        body {
          font-size: 16px !important;
          font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji !important;
        }
        .main-container {
          width: 100%;
          padding: 0;
        }
        .ant-col {
          font-size: 16px !important;
        }
        .h1, .h2, .h3, .h4, .h5, .h6, h1, h2, h3, h4, h5, h6 {
          margin-bottom: .5rem;
          font-family: inherit;
          font-weight: 500;
          font-size: 16px;
          line-height: 1.2;
          color: inherit;
        }
        table {
          color: black;
 
          text-align: center;
          border-collapse: collapse;
        }
        table, th, td {
          text-align: center;
          border-collapse: collapse;
        }
        .table-bordered {
          border: 1px solid #dee2e6;
        }
        .table-bordered td, .table-bordered th {
          border: 1px solid #dee2e6;
        }
        .table-bordered td, .table-bordered thead th {
          border-bottom-width: 2px;
        }
        .table thead th {
          vertical-align: bottom;
          border-bottom: 2px solid #dee2e6;
        }
        .table-bordered th {
          border: 1px solid #dee2e6;
        }
        .table td, .table th {
          padding: .75rem;
          vertical-align: top;
          border-top: 1px solid #dee2e6;
        }
        .header-section {
          text-align: center;
          margin-bottom: 20px;
        }
        .main-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }
        .sub-header {
          font-size: 24px;
          font-weight: bold;
          margin: 0;
        }
        .copy-label {
          float: right;
          font-size: 18px;
          font-weight: normal;
        }
        .info-section {
          display: flex;
          justify-content: space-between;
          margin: 20px 0;
          font-size: 16px;
        }
        .info-section div {
          font-size: 16px;
        }
        .signature-section {
          margin-top: 100px;
          font-size: 16px;
        }
  `;

    const memoPrintStyles = useMemo(() => printStyles, [printStyles]);
    const memoCommonStyles = useMemo(() => commonStyles, [commonStyles]);

    useEffect(() => {
        const styleEl = document.createElement('style');
        styleEl.innerHTML = memoPrintStyles + memoCommonStyles;
        document.head.appendChild(styleEl);
        return () => document.head.removeChild(styleEl);
    }, [memoPrintStyles, memoCommonStyles]);

    const handlePrint = () => {
        window.print();
    };

    return (
        <>
            <div
                className="print-container-quotation"
                style={{ padding: '20px', background: 'white' }}
                ref={contentRef}
            >
                <div className="page-content">
                    <Row
                        justify="space-between"
                        align="middle"
                        style={{ marginBottom: '40px', marginTop: '5px' }}
                    >
                        <Col
                            span={24}
                            className="logo-section"
                            style={{ textAlign: 'right' }}
                        >
                            <img src={logo} alt="Company Logo" style={{ maxWidth: '180px' }} />
                        </Col>
                    </Row>
                    <Row
                        justify="space-between"
                        align="middle"
                        style={{ marginBottom: '40px' }}
                    >
                        <Col span={24}>
                            <div style={{ textAlign: 'center' }}>
                                <h1 className="main-heading">
                                    Quotation
                                </h1>
                            </div>
                        </Col>
                    </Row>

                    <Row
                        justify="space-between"
                        align="top"
                        style={{ marginBottom: '40px', width: '100%' }}
                    >
                        <Col span={12}>
                            <div className="customer-details">
                                <h6 className="customer-details-header">Bill To</h6>
                                <span className="header-text">
                                    {quotationData?.customer?.customer_name}
                                    <br />
                                    {quotationData?.customer?.office_address?.shop},{' '}
                                    {quotationData?.customer?.office_address?.area},{' '}
                                    {quotationData?.customer?.office_address?.landmark}{' '}
                                    {quotationData?.customer?.office_address?.city}{' '}
                                    {quotationData?.customer?.office_address?.state} -{' '}
                                    {quotationData?.customer?.office_address?.pin_code}
                                </span>
                            </div>
                        </Col>
                        <Col
                            span={12}
                            style={{ textAlign: 'right' }}
                            className="date-quotation"
                        >
                            <div>
                                <span className="header-text">
                                    <strong>Date: </strong>
                                    {quotationData?.quotation_date
                                        ? dayjs(quotationData.quotation_date).format('DD-MM-YYYY')
                                        : 'N/A'}
                                </span>
                            </div>
                            <div>
                                <span className="header-text">
                                    <strong>Quotation No: </strong>
                                    {quotationData?.quotation_no || 'N/A'}
                                </span>
                            </div>
                        </Col>
                    </Row>

                    <Row
                        justify="space-between"
                        align="top"
                        style={{ marginBottom: '10px', width: '100%' }}
                    >
                        <Col span={12}>
                            <div className="customer-details">
                                <h6 className="customer-details-header">Ship To</h6>
                                <div className="customer-details-content">
                                    <span className="header-text">
                                        {quotationData?.customer?.customer_name}
                                        <br />
                                        {quotationData?.customer?.factory_address?.shop},{' '}
                                        {quotationData?.customer?.factory_address?.area},{' '}
                                        {quotationData?.customer?.factory_address?.landmark}{' '}
                                        {quotationData?.customer?.factory_address?.city}{' '}
                                        {quotationData?.customer?.factory_address?.state} -{' '}
                                        {quotationData?.customer?.factory_address?.pin_code}
                                    </span>
                                </div>
                            </div>
                        </Col>
                        <Col
                            span={12}
                            style={{ textAlign: 'right' }}
                            className="kind-attention"
                        >
                            <div className="customer-details">
                                <div className="customer-details-header">Kind Attention</div>
                                <div className="customer-details-content">
                                    <span className="header-text">
                                        {quotationData?.customer?.contact_persons?.[0]
                                            ?.contact_person_name || 'N/A'}
                                        <br />
                                        {quotationData?.customer?.contact_persons?.[0]
                                            ?.contact_person_mobile_number
                                            ? `+${quotationData.customer.contact_persons[0].contact_person_mobile_number}`
                                            : 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <div style={{ width: '100%', marginBottom: '10px' }}>
                        <table
                            style={{
                                width: '100%',
                                border: '1px solid #dee2e6',
                                marginTop: '30px',
                                borderCollapse: 'collapse'
                            }}
                        >
                            <thead>{renderHeaderRows()}</thead>
                            <tbody>
                                {quotationData?.quotation_details?.map((record, rowIndex) => (
                                    <tr key={record.id || rowIndex}>
                                        {leafColumns.map((column, colIndex) => (
                                            <td
                                                key={column.key || colIndex}
                                                className={column.className}
                                                style={{
                                                    border: '1px solid #dee2e6',
                                                    padding: '12px 8px',
                                                    textAlign: column.align || 'center',
                                                    fontSize: '14px'
                                                }}
                                            >
                                                {renderCellContent(column, record, rowIndex)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                                <tr className="summary-row">
                                    <td
                                        colSpan={6}
                                        style={{
                                            border: '1px solid #dee2e6',
                                            backgroundColor: '#e9ecef',
                                            fontWeight: '600',
                                            textAlign: 'center'
                                        }}
                                    >
                                        <strong>Total</strong>
                                    </td>
                                    <td
                                        style={{
                                            border: '1px solid #dee2e6',
                                            backgroundColor: '#e9ecef',
                                            fontWeight: '600',
                                            textAlign: 'center'
                                        }}
                                    >
                                        <strong>{quotationData?.total_pieces} Nos.</strong>
                                    </td>
                                    <td
                                        style={{
                                            border: '1px solid #dee2e6',
                                            backgroundColor: '#e9ecef',
                                            fontWeight: '600',
                                            textAlign: 'center'
                                        }}
                                    >
                                        <strong>{quotationData?.total_net_weight} Kg.</strong>
                                    </td>
                                    {jobWorkLeafCount > 0 && (
                                        <td
                                            colSpan={
                                                hasOnlyMillFinish
                                                    ? jobWorkLeafCount + 2
                                                    : jobWorkLeafCount + 3
                                            }
                                            style={{
                                                border: '1px solid #dee2e6',
                                                backgroundColor: '#e9ecef'
                                            }}
                                        />
                                    )}
                                    <td
                                        style={{
                                            border: '1px solid #dee2e6',
                                            backgroundColor: '#e9ecef',
                                            fontWeight: '600',
                                            textAlign: 'right'
                                        }}
                                    >
                                        <strong>{quotationData?.total_basic_amount?.toFixed(2)}</strong>
                                    </td>
                                    <td
                                        style={{
                                            border: '1px solid #dee2e6',
                                            backgroundColor: '#e9ecef',
                                            fontWeight: '600',
                                            textAlign: 'right'
                                        }}
                                    >
                                        <strong>{quotationData?.total_gst_amount?.toFixed(2)}</strong>
                                    </td>
                                    <td
                                        style={{
                                            border: '1px solid #dee2e6',
                                            backgroundColor: '#e9ecef',
                                            fontWeight: '600',
                                            textAlign: 'right'
                                        }}
                                    >
                                        <strong>{quotationData?.total_amount?.toFixed(2)}</strong>
                                    </td>
                                </tr>
                                <tr className="summary-row">
                                    <td
                                        colSpan={leafColumns.length - 1}
                                        style={{
                                            border: '1px solid #dee2e6',
                                            backgroundColor: '#e9ecef',
                                            fontWeight: '600',
                                            textAlign: 'right'
                                        }}
                                    >
                                        <strong>Total Payable Amount :</strong>
                                    </td>
                                    <td
                                        style={{
                                            border: '1px solid #dee2e6',
                                            backgroundColor: '#e9ecef',
                                            fontWeight: '600',
                                            textAlign: 'right'
                                        }}
                                    >
                                        <strong>{quotationData?.total_amount?.toFixed(2)}</strong>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="signature-section" style={{ marginTop: '80px', display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ textAlign: 'center', flex: 1 }}>
                            <Title
                                level={5}
                                style={{ color: '#12406a', margin: '5px 0', marginTop: '60px' }}
                            >
                                Prepared By
                            </Title>
                        </div>
                        <div style={{ textAlign: 'center', flex: 1 }}>
                            <Title
                                level={5}
                                style={{ color: '#12406a', margin: '5px 0', marginTop: '60px' }}
                            >
                                Authorised Signatory
                                <br />
                                (Alux ERP)
                            </Title>
                        </div>
                    </div>
                </div>
            </div>

            <div
                className="terms-and-bank-container"
                style={{
                    padding: '20px',
                    pageBreakBefore: 'always',
                    background: 'white'
                }}
            >
                <div className="terms-content-wrapper">
                    <Row justify="center">
                        <h4 className="terms-heading">
                            Terms & Conditions
                        </h4>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <div
                                className="terms__content"
                                dangerouslySetInnerHTML={{
                                    __html:
                                        quotationData?.terms_and_condition
                                            ?.replace(/<p>( |\s)*<\/p>/gi, '')
                                            .replace(/\s+/g, ' ')
                                            .trim() || 'Terms and conditions not available',
                                }}
                            />
                        </Col>
                    </Row>
                </div>

                <div className="bank-details-wrapper">
                    <Row justify="start" style={{ marginTop: '20px' }}>
                        <Col md={24}>
                            <div className="bankDetails">
                                <Text strong style={{ fontSize: '16px', fontWeight: 'bold' }}>
                                    Bank Details
                                </Text>
                                <div style={{ lineHeight: 1.6, fontSize: '16px', marginTop: '10px' }}>
                                    <div>
                                        <strong>Name: </strong> ICICI BANK LTD &nbsp;&nbsp;&nbsp;&nbsp;
                                        <strong>Branch: </strong> Vadodara, Gujarat
                                    </div>
                                    <div>
                                        <strong>A/C: </strong> 000305501851 &nbsp;&nbsp;&nbsp;&nbsp;
                                        <strong>IFSC: </strong> ICIC0000003
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            <div
                className="no-print"
                style={{
                    textAlign: 'center',
                    marginTop: '20px',
                    padding: '20px',
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    zIndex: 1000
                }}
            >
                <Button
                    type="primary"
                    size="large"
                    onClick={handlePrint}
                    icon={<PrintIcon />}
                    style={{
                        boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)',
                        borderRadius: '8px'
                    }}
                >
                    Print Quotation
                </Button>
            </div>
        </>
    );
};

export default PrintQuotation3;